import * as React from 'react';
import { useState } from 'react';
import { Select, Tag } from 'antd';
import { connect } from 'react-redux';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import useMountEffect from '../../hooks/useMountEffect';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import { dispatchersRestCrudThunksForSupplier } from '../../thunks/dispatchers_thunks';

const ROLE_TAG_PROPS = {
	dispatcher: {
		label: 'Dispatcher',
		className: '!ml-2 border border-solid !border-green-4/60 !bg-green-2/60 !text-grey-8',
	},
	leadTech: {
		label: 'Lead Tech',
		className: '!ml-2 border border-solid !border-purple-3/60 !bg-purple-2/60 !text-grey-8',
	},
	cc: {
		label: 'CC',
		className: '!ml-2 border border-solid !border-cyan-7/30 !bg-cyan-7/10 !text-grey-8',
	},
};

interface FieldTechSelectProps {
	locationId: any;
	supplierFacilityId: any;
	isReschedule?: boolean;
	mode?: 'multiple';
	allowClear?: boolean;
	targetCollectionName: string;
	value?: any;
	onChange?: any;
	fieldTechs: any;
	fetchFieldTechs: any;
	fetchMultipleFieldTechs: any;
	fetchSupplierDispatcher: any;
}

const FieldTechSelect: React.FC<FieldTechSelectProps> = (props) => {
	const {
		locationId,
		supplierFacilityId,
		isReschedule,
		mode,
		allowClear,
		targetCollectionName,
		value,
		onChange,
		fieldTechs,
		fetchFieldTechs,
		fetchMultipleFieldTechs,
		fetchSupplierDispatcher,
	} = props;

	const [supplierDispatcher, setSupplierDispatcher] = useState<any>(null);
	const [dispatcherFieldTechs, setDispatcherFieldTechs] = useState<any[]>([]);
	const [loadingDispatcher, setLoadingDispatcher] = useState(false);

	useMountEffect(() => {
		if (!locationId || !supplierFacilityId) return;
		const dispatcherCollection = isReschedule
			? 'rescheduleServiceCallDispatcher'
			: 'scheduleServiceCallDispatcher';
		setLoadingDispatcher(true);
		fetchSupplierDispatcher({ locationId, supplierFacilityId }, dispatcherCollection)
			.then((results) => {
				const matchingRecord = results && results.find((r) => r.locationId === locationId);
				if (!matchingRecord) {
					setLoadingDispatcher(false);
					return;
				}
				setSupplierDispatcher(matchingRecord);
				const emails = [
					matchingRecord.dispatcher,
					...nullSafeGetOrElse('primaryTechList', matchingRecord, []),
					...nullSafeGetOrElse('ccList', matchingRecord, []),
				].filter(Boolean);
				const uniqueEmails = Array.from(new Set(emails));
				if (uniqueEmails.length === 0) {
					setLoadingDispatcher(false);
					return;
				}
				fetchMultipleFieldTechs(uniqueEmails, 'scheduleServiceCallDispatcherRoles')
					.then((records) => {
						setDispatcherFieldTechs(records || []);
						setLoadingDispatcher(false);
					})
					.catch(() => setLoadingDispatcher(false));
			})
			.catch(() => setLoadingDispatcher(false));
	});

	const getTechRole = (email: string): 'dispatcher' | 'leadTech' | 'cc' | null => {
		if (!supplierDispatcher || !email) return null;
		if (email === supplierDispatcher.dispatcher) return 'dispatcher';
		if (nullSafeGetOrElse('primaryTechList', supplierDispatcher, []).includes(email))
			return 'leadTech';
		if (nullSafeGetOrElse('ccList', supplierDispatcher, []).includes(email)) return 'cc';
		return null;
	};

	const orderTechs = (records: any[]): any[] => {
		if (!supplierDispatcher) return records;
		const dispatcher = supplierDispatcher.dispatcher;
		const primaryTechList = nullSafeGetOrElse('primaryTechList', supplierDispatcher, []);
		const ccList = nullSafeGetOrElse('ccList', supplierDispatcher, []);
		const existingEmails = new Set(
			records.map((ft) => nullSafeGet('supplierContact.email', ft)).filter(Boolean)
		);
		const merged = [
			...records,
			...dispatcherFieldTechs.filter((ft) => {
				const email = nullSafeGet('supplierContact.email', ft);
				return email && !existingEmails.has(email);
			}),
		];
		const priority = (ft: any) => {
			const email = nullSafeGet('supplierContact.email', ft);
			if (primaryTechList.includes(email)) return 0;
			if (email === dispatcher) return 1;
			if (ccList.includes(email)) return 2;
			return 3;
		};
		return merged.sort((first, second) => priority(first) - priority(second));
	};

	return (
		<OWAsyncSelect
			mode={mode}
			value={value}
			onChange={onChange}
			stateSlice={fieldTechs}
			valueAccessor={(el) => el.email}
			allowClear={allowClear}
			loading={loadingDispatcher}
			disabled={loadingDispatcher}
			targetCollectionName={targetCollectionName}
			fetchData={(
				searchText,
				collectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			) => {
				fetchFieldTechs(
					{ search: searchText && searchText.trim() },
					collectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				);
			}}
			fetchMultiple={(ids, collectionName) => {
				fetchMultipleFieldTechs(ids, collectionName);
			}}
			orderRecords={orderTechs}
			renderRecord={(ft) => {
				const email = nullSafeGet('supplierContact.email', ft);
				const role = getTechRole(email);
				return (
					<Select.Option key={ft.email} value={ft.email}>
						{nullSafeGet('supplierContact.nameGiven', ft)}{' '}
						{nullSafeGet('supplierContact.nameFamily', ft)} ({email})
						{role && (
							<Tag className={ROLE_TAG_PROPS[role].className}>{ROLE_TAG_PROPS[role].label}</Tag>
						)}
					</Select.Option>
				);
			}}
			sortBy={{
				sort_by: 'name',
				order: 'ascend',
			}}
		/>
	);
};

const mapStateToProps = (state) => ({
	fieldTechs: state.field_techs,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleFieldTechs: (ids, targetCollectionName) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchSupplierDispatcher: (params, targetCollectionName) =>
		dispatch(dispatchersRestCrudThunksForSupplier.read(params, targetCollectionName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FieldTechSelect);
